// ── Plotly chart wrappers — dark theme, on-brand ──────────────────────
// All charts: transparent bg, Inter/Geist Mono fonts, full interactivity.

const PLOTLY_LAYOUT_BASE = {
  paper_bgcolor: "rgba(0,0,0,0)",
  plot_bgcolor:  "rgba(0,0,0,0)",
  font:    { family: "Inter, Inter Tight, sans-serif", color: "#f5f5f7", size: 11 },
  margin:  { l: 52, r: 18, t: 14, b: 38 },
  hovermode: "x unified",
  hoverlabel: { bgcolor: "#161616", bordercolor: "rgba(255,255,255,0.14)", font: { family: "Geist Mono, monospace", color: "#f5f5f7", size: 11 } },
  xaxis: { gridcolor: "rgba(255,255,255,0.05)", linecolor: "rgba(255,255,255,0.10)", zerolinecolor: "rgba(255,255,255,0.08)", tickfont: { family: "Geist Mono, monospace", size: 10, color: "rgba(245,245,247,0.55)" } },
  yaxis: { gridcolor: "rgba(255,255,255,0.05)", linecolor: "rgba(255,255,255,0.10)", zerolinecolor: "rgba(255,255,255,0.08)", tickfont: { family: "Geist Mono, monospace", size: 10, color: "rgba(245,245,247,0.55)" } },
  showlegend: false,
};

const PLOTLY_CONFIG = {
  displaylogo: false,
  responsive: true,
  toImageButtonOptions: { format: "png", filename: "gq_chart", scale: 2 },
  modeBarButtonsToRemove: ["lasso2d","select2d","autoScale2d","toggleSpikelines"],
  displayModeBar: "hover",
};

const C = {
  strategy:  "#11ff99",
  benchmark: "#D946EF",
  cta:       "#11ff99",
  drawdown:  "#ff2047",
  amber:     "#3b9eff",
  blue:      "#3b9eff",
};

// Hook to render a Plotly chart into a ref. Plotly is loaded from CDN as window.Plotly.
function usePlot(ref, data, layout, config = PLOTLY_CONFIG) {
  React.useEffect(() => {
    if (!ref.current || !window.Plotly) return;
    window.Plotly.newPlot(ref.current, data, { ...PLOTLY_LAYOUT_BASE, ...layout }, config);
    return () => { try { window.Plotly.purge(ref.current); } catch {} };
  }, [data, layout]);
}

// ── Equity curve (with optional benchmark overlay) ───────────────────
function EquityChart({ equity, benchmark, dates, height = 280 }) {
  const ref = React.useRef(null);
  const x = dates && dates.length === equity.length ? dates : equity.map((_,i)=>i);
  const traces = [{
    x, y: equity, type: "scatter", mode: "lines", name: "Strategy",
    line: { color: C.strategy, width: 2, shape: "spline" },
    fill: "tozeroy", fillcolor: "rgba(17,255,153,0.08)",
    hovertemplate: "<b>%{y:.2f}</b><extra></extra>",
  }];
  if (benchmark) {
    traces.unshift({
      x, y: benchmark, type: "scatter", mode: "lines", name: "Benchmark",
      line: { color: C.benchmark, width: 1.2, dash: "dot" },
      hovertemplate: "Benchmark %{y:.2f}<extra></extra>",
    });
  }
  usePlot(ref, traces, { height });
  return <div ref={ref} style={{ width:"100%", height }}/>;
}

// ── Drawdown (underwater) ────────────────────────────────────────────
function DrawdownChart({ dds, dates, height = 140 }) {
  const ref = React.useRef(null);
  const x = dates && dates.length === dds.length ? dates : dds.map((_,i)=>i);
  usePlot(ref, [{
    x, y: dds, type: "scatter", mode: "lines",
    line: { color: C.drawdown, width: 1.5, shape: "spline" },
    fill: "tozeroy", fillcolor: "rgba(255,32,71,0.20)",
    hovertemplate: "%{y:.2f}%<extra></extra>",
  }], { height, yaxis: { ...PLOTLY_LAYOUT_BASE.yaxis, ticksuffix:"%" } });
  return <div ref={ref} style={{ width:"100%", height }}/>;
}

// ── Monthly returns heatmap ───────────────────────────────────────────
const MONTH_LBL = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function MonthlyHeatmapChart({ monthly, height = 220 }) {
  const ref = React.useRef(null);
  // monthly: object { "YYYY-MM": pct }
  const keys = Object.keys(monthly).sort();
  const years = [...new Set(keys.map(k=>k.slice(0,4)))].sort();
  const z = years.map(y => Array.from({length:12}, (_, mi) => {
    const v = monthly[`${y}-${String(mi+1).padStart(2,"0")}`];
    return v === undefined ? null : v;
  }));
  usePlot(ref, [{
    z, x: MONTH_LBL, y: years, type: "heatmap",
    colorscale: [
      [0,    "#ff2047"],
      [0.35, "rgba(255,32,71,0.25)"],
      [0.5,  "rgba(255,255,255,0.04)"],
      [0.65, "rgba(17,255,153,0.25)"],
      [1,    "#11ff99"],
    ],
    zmid: 0,
    showscale: false,
    hovertemplate: "%{y} %{x}<br><b>%{z:.2f}%</b><extra></extra>",
    xgap: 2, ygap: 2,
  }], { height, margin:{l:50,r:18,t:14,b:30}, xaxis:{ ...PLOTLY_LAYOUT_BASE.xaxis, side:"top" }, yaxis: { ...PLOTLY_LAYOUT_BASE.yaxis, autorange: "reversed" } });
  return <div ref={ref} style={{ width:"100%", height }}/>;
}

// ── Return distribution histogram ────────────────────────────────────
function DistributionChart({ returns, height = 180 }) {
  const ref = React.useRef(null);
  usePlot(ref, [{
    x: returns, type: "histogram", nbinsx: 30,
    marker: { color: returns.map(r => r >= 0 ? "rgba(17,255,153,0.55)" : "rgba(255,32,71,0.55)"), line: { color: "rgba(255,255,255,0.08)", width: 0.5 } },
    hovertemplate: "Bin: %{x:.2f}%<br>Count: %{y}<extra></extra>",
  }], { height, yaxis:{...PLOTLY_LAYOUT_BASE.yaxis, title:""}, bargap: 0.05 });
  return <div ref={ref} style={{ width:"100%", height }}/>;
}

// ── Fitness evolution (Optimize) ─────────────────────────────────────
function FitnessChart({ history, generation, total, height = 220 }) {
  const ref = React.useRef(null);
  usePlot(ref, [{
    x: history.map((_,i)=>i+1),
    y: history.map(v => v*100),
    type: "scatter", mode: "lines+markers",
    line: { color: C.cta, width: 2, shape: "spline" },
    marker: { color: C.cta, size: 4 },
    fill: "tozeroy", fillcolor: "rgba(17,255,153,0.10)",
    hovertemplate: "Gen %{x}<br>Fitness <b>%{y:.2f}%</b><extra></extra>",
  }], {
    height,
    xaxis: { ...PLOTLY_LAYOUT_BASE.xaxis, range: [0, total||40], title:{ text:"Generation", font:{ size:10, color:"rgba(245,245,247,0.4)" } } },
    yaxis: { ...PLOTLY_LAYOUT_BASE.yaxis, ticksuffix:"%", title:{ text:"Fitness", font:{ size:10, color:"rgba(245,245,247,0.4)" } } },
  });
  return <div ref={ref} style={{ width:"100%", height }}/>;
}

// ── Stress scenario comparison ────────────────────────────────────────
function StressBarChart({ scenarios, height = 260 }) {
  const ref = React.useRef(null);
  const names = scenarios.map(s => s.name);
  const mktDD = scenarios.map(s => parseFloat(s.mktDD)||0);
  const strDD = scenarios.map(s => parseFloat(s.strDD)||0);
  usePlot(ref, [
    { x: names, y: mktDD, type:"bar", name:"Market DD",   marker:{color:"rgba(255,32,71,0.55)"}, hovertemplate:"%{x}<br>Mkt DD <b>%{y}%</b><extra></extra>" },
    { x: names, y: strDD, type:"bar", name:"Strategy DD", marker:{color:"rgba(59,158,255,0.85)"}, hovertemplate:"%{x}<br>Str DD <b>%{y}%</b><extra></extra>" },
  ], {
    height, barmode:"group", showlegend:true,
    legend:{ x:0, y:1.12, orientation:"h", font:{ size:10, color:"rgba(245,245,247,0.6)" }, bgcolor:"transparent" },
    yaxis:{ ...PLOTLY_LAYOUT_BASE.yaxis, ticksuffix:"%" }
  });
  return <div ref={ref} style={{ width:"100%", height }}/>;
}

// ── Compare overlay (multi equity) ────────────────────────────────────
function CompareEquityChart({ a, b, nameA, nameB, height = 260 }) {
  const ref = React.useRef(null);
  usePlot(ref, [
    { x: a.map((_,i)=>i), y: a, type:"scatter", mode:"lines", name: nameA, line:{ color: C.strategy, width: 2, shape:"spline" } },
    { x: b.map((_,i)=>i), y: b, type:"scatter", mode:"lines", name: nameB, line:{ color: C.benchmark, width: 2, shape:"spline" } },
  ], {
    height, showlegend:true,
    legend:{ x:0, y:1.12, orientation:"h", font:{ size:11, color:"rgba(245,245,247,0.6)" }, bgcolor:"transparent" },
  });
  return <div ref={ref} style={{ width:"100%", height }}/>;
}

// ────────────────────────────────────────────────────────────────────────
// MC Fan Chart — percentile bands (5/25/50/75/95) from Monte-Carlo paths
// ────────────────────────────────────────────────────────────────────────
function FanChart({ bands, percentiles = [0.05, 0.25, 0.50, 0.75, 0.95], height = 280 }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current || !window.Plotly || !bands) return;
    const x = bands[0].map((_, i) => i);
    const traces = [
      // Outer band (5-95) — light fill
      { x, y: bands[4], type:"scatter", mode:"lines", name:"95th pct",
        line:{ color:"rgba(217,70,239,0.4)", width:0 } },
      { x, y: bands[0], type:"scatter", mode:"lines", name:"5th pct",
        line:{ color:"rgba(217,70,239,0.4)", width:0 },
        fill:"tonexty", fillcolor:"rgba(217,70,239,0.10)" },
      // Inner band (25-75)
      { x, y: bands[3], type:"scatter", mode:"lines", name:"75th pct",
        line:{ color:"rgba(59,158,255,0.5)", width:0 } },
      { x, y: bands[1], type:"scatter", mode:"lines", name:"25th pct",
        line:{ color:"rgba(59,158,255,0.5)", width:0 },
        fill:"tonexty", fillcolor:"rgba(59,158,255,0.15)" },
      // Median
      { x, y: bands[2], type:"scatter", mode:"lines", name:"Median",
        line:{ color:"#11ff99", width:2.2, shape:"spline" } },
    ];
    window.Plotly.newPlot(ref.current, traces, {
      paper_bgcolor:"rgba(0,0,0,0)", plot_bgcolor:"rgba(0,0,0,0)",
      font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},
      margin:{l:45,r:15,t:15,b:35}, height,
      xaxis:{ gridcolor:"rgba(255,255,255,0.05)", title:{text:"Day",font:{size:10,color:"rgba(245,245,247,0.4)"}}, tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"} },
      yaxis:{ gridcolor:"rgba(255,255,255,0.05)", title:{text:"Equity (×)",font:{size:10,color:"rgba(245,245,247,0.4)"}}, tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"} },
      showlegend:true, legend:{ x:0, y:1.12, orientation:"h", font:{size:10}, bgcolor:"transparent" },
    }, { displayModeBar:false, responsive:true });
    return () => { try { window.Plotly.purge(ref.current); } catch(e){} };
  }, [bands]);
  return <div ref={ref} style={{ width:"100%", height }} />;
}

// ────────────────────────────────────────────────────────────────────────
// Pareto Frontier — Sharpe vs Max DD scatter with dominance shading
// ────────────────────────────────────────────────────────────────────────
function ParetoChart({ paretoFront, dominated = [], height = 320 }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current || !window.Plotly) return;
    const traces = [];
    if (dominated.length) {
      traces.push({
        x: dominated.map(p => p.objectives[0]),
        y: dominated.map(p => p.objectives[1] * 100),
        type:"scatter", mode:"markers", name:"Dominated",
        marker:{ size:5, color:"rgba(245,245,247,0.18)" },
        hovertemplate:"Sharpe %{x:.2f}<br>MaxDD %{y:.1f}%<extra></extra>",
      });
    }
    if (paretoFront?.length) {
      const sorted = [...paretoFront].sort((a, b) => a.objectives[1] - b.objectives[1]);
      traces.push({
        x: sorted.map(p => p.objectives[0]),
        y: sorted.map(p => p.objectives[1] * 100),
        type:"scatter", mode:"lines+markers", name:"Pareto frontier",
        line:{ color:"#11ff99", width:2 },
        marker:{ size:9, color:"#11ff99", line:{color:"#fff",width:1.5} },
        hovertemplate:"<b>Pareto</b><br>Sharpe %{x:.2f}<br>MaxDD %{y:.1f}%<extra></extra>",
      });
    }
    window.Plotly.newPlot(ref.current, traces, {
      paper_bgcolor:"rgba(0,0,0,0)", plot_bgcolor:"rgba(0,0,0,0)",
      font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},
      margin:{l:50,r:20,t:20,b:40}, height,
      xaxis:{ gridcolor:"rgba(255,255,255,0.05)", title:{text:"Sharpe ratio (higher = better)",font:{size:10,color:"rgba(245,245,247,0.4)"}}, tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"} },
      yaxis:{ gridcolor:"rgba(255,255,255,0.05)", title:{text:"Max Drawdown % (lower = better)",font:{size:10,color:"rgba(245,245,247,0.4)"}}, tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"} },
      showlegend:true, legend:{ x:0.02, y:0.98, font:{size:10}, bgcolor:"rgba(0,0,0,0.3)" },
    }, { displayModeBar:false, responsive:true });
    return () => { try { window.Plotly.purge(ref.current); } catch(e){} };
  }, [paretoFront, dominated]);
  return <div ref={ref} style={{ width:"100%", height }} />;
}

// ────────────────────────────────────────────────────────────────────────
// Parallel Coordinates — multi-param sensitivity surfaces
// ────────────────────────────────────────────────────────────────────────
function ParallelCoordinatesChart({ candidates, dimensions, height = 280 }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current || !window.Plotly || !candidates?.length || !dimensions?.length) return;
    const dims = dimensions.map(d => ({
      label: d.label,
      values: candidates.map(c => c.params[d.key]),
      range: d.range,
    }));
    // Add fitness colour
    const trace = {
      type: 'parcoords',
      line: { color: candidates.map(c => c.fitness), colorscale: [[0, '#8b5cf6'], [0.5, '#3b9eff'], [1, '#11ff99']], showscale: true },
      dimensions: dims,
    };
    window.Plotly.newPlot(ref.current, [trace], {
      paper_bgcolor:"rgba(0,0,0,0)", plot_bgcolor:"rgba(0,0,0,0)",
      font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},
      margin:{l:60,r:30,t:30,b:40}, height,
    }, { displayModeBar:false, responsive:true });
    return () => { try { window.Plotly.purge(ref.current); } catch(e){} };
  }, [candidates, dimensions]);
  return <div ref={ref} style={{ width:"100%", height }} />;
}

// ────────────────────────────────────────────────────────────────────────
// Sensitivity Heatmap — param × scenario survival
// ────────────────────────────────────────────────────────────────────────
function SensitivityHeatmap({ z, x, y, height = 280 }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current || !window.Plotly) return;
    window.Plotly.newPlot(ref.current, [{
      z, x, y, type:"heatmap",
      colorscale:[[0, '#f87171'], [0.5, '#3b9eff'], [1, '#11ff99']],
      hovertemplate:"<b>%{y} · %{x}</b><br>Score %{z:.2f}<extra></extra>",
    }], {
      paper_bgcolor:"rgba(0,0,0,0)", plot_bgcolor:"rgba(0,0,0,0)",
      font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},
      margin:{l:120,r:20,t:20,b:40}, height,
      xaxis:{ tickfont:{size:9,color:"rgba(245,245,247,0.6)"} },
      yaxis:{ tickfont:{size:10,color:"rgba(245,245,247,0.7)"} },
    }, { displayModeBar:false, responsive:true });
    return () => { try { window.Plotly.purge(ref.current); } catch(e){} };
  }, [z, x, y]);
  return <div ref={ref} style={{ width:"100%", height }} />;
}

window.EquityChart        = EquityChart;
window.DrawdownChart      = DrawdownChart;
window.MonthlyHeatmapChart= MonthlyHeatmapChart;
window.DistributionChart  = DistributionChart;
window.FitnessChart       = FitnessChart;
window.StressBarChart     = StressBarChart;
window.CompareEquityChart = CompareEquityChart;
window.FanChart           = FanChart;
window.ParetoChart        = ParetoChart;
window.ParallelCoordinatesChart = ParallelCoordinatesChart;
window.SensitivityHeatmap = SensitivityHeatmap;

// Shared Plotly defaults — pages spread these and override per-chart bits
window.PLOTLY_LAYOUT_BASE = PLOTLY_LAYOUT_BASE;
window.PLOTLY_CONFIG      = PLOTLY_CONFIG;
